import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import {
    MlaAuthService,
    MlaBorrowerService,
    MlaDashboardService,
    OriginationTokenRefreshService,
    SsoAuthService,
} from '@ld.consumer-portal/data-access';
import { AuthService, LoggingService } from '@ld.consumer-portal/utils';

export function stripEpsLoanPrefix(id: string): string {
  return id.startsWith('1_MTG_') ? id.substring(6) : id;
}

export const originationAuthGuard: CanActivateFn = async (route) => {
  const loggingService = inject(LoggingService);
  const authService = inject(AuthService);
  const ssoAuthService = inject(SsoAuthService);
  const mlaAuthService = inject(MlaAuthService);
  const mlaBorrowerService = inject(MlaBorrowerService);
  const mlaDashboardService = inject(MlaDashboardService);
  const tokenRefreshService = inject(OriginationTokenRefreshService);
  const router = inject(Router);

  const loanId = route.paramMap.get('loanId');
  if (!loanId) {
    loggingService.logWarning('[OriginationAuthGuard] No loanId in route params, redirecting to dashboard');
    return router.createUrlTree(['/dashboard']);
  }

  const b2cIdToken = authService.getB2cIdToken();
  if (!b2cIdToken) {
    loggingService.logWarning('[OriginationAuthGuard] No B2C token available');
    return router.createUrlTree(['/login']);
  }

  try {
    const hadMlaSession = mlaAuthService.hasValidSession();

    const mlaSessionValid = await mlaAuthService.ensureMlaSession();
    if (!mlaSessionValid) {
      loggingService.logWarning('[OriginationAuthGuard] Failed to establish MLA session, redirecting to dashboard');
      return router.createUrlTree(['/dashboard']);
    }

    const borrowerSwitchLoanId = sessionStorage.getItem('cp_borrower_switch_pending');
    if (borrowerSwitchLoanId) {
      sessionStorage.removeItem('cp_borrower_switch_pending');
    }
    const borrowerSwitchMatchesRoute = !!borrowerSwitchLoanId && stripEpsLoanPrefix(borrowerSwitchLoanId) === stripEpsLoanPrefix(loanId);

    const storedCpLoanId = authService.getCpLoanId();
    const loanIdsMatch = !!storedCpLoanId && stripEpsLoanPrefix(storedCpLoanId) === stripEpsLoanPrefix(loanId);
    if (hadMlaSession && authService.hasCpContext() && !authService.isCpTokenExpired() && (loanIdsMatch || borrowerSwitchMatchesRoute)) {
      loggingService.logInfo(
        `[OriginationAuthGuard] Reusing existing CP context (storedCpLoanId=${storedCpLoanId}, routeLoanId=${loanId}, borrowerSwitchMatchesRoute=${borrowerSwitchMatchesRoute})`,
      );
      tokenRefreshService.startMonitoring();
      if (storedCpLoanId && stripEpsLoanPrefix(storedCpLoanId) !== stripEpsLoanPrefix(loanId)) {
        return router.createUrlTree(['/origination', storedCpLoanId]);
      }
      return true;
    }

    loggingService.logInfo(
      `[OriginationAuthGuard] CP context check failed, re-authenticating (hadMlaSession=${hadMlaSession}, hasCpContext=${authService.hasCpContext()}, isCpTokenExpired=${authService.isCpTokenExpired()}, storedCpLoanId=${storedCpLoanId}, routeLoanId=${loanId})`,
    );

    const borrowerInfo = await mlaBorrowerService.getBorrowerInfo();

    const mlaBorrowerId = sessionStorage.getItem('mla_borrowerId');
    const validMlaBorrowerId = mlaBorrowerId && mlaBorrowerId !== 'undefined' ? mlaBorrowerId : null;
    const selectedBorrower = validMlaBorrowerId
      ? borrowerInfo.find(
          (b) =>
            b.borrowerIds?.includes(validMlaBorrowerId) || b.borrowerInfoId === validMlaBorrowerId,
        )
      : null;
    const borrower = selectedBorrower || borrowerInfo.find((b) => b.isPrimary);

    if (borrower?.firstName || borrower?.lastName) {
      const userId = authService.getUserId() || '';
      authService.setUserInfo(userId, borrower.firstName, borrower.lastName);
    }

    const dashboardBorrowerId = mlaDashboardService.getLoggedInBorrowerIdForLoan(loanId);
    const normalizedRouteLoanId = stripEpsLoanPrefix(loanId);
    const loanDetail = borrower?.loanDetails?.find(
      (ld) => stripEpsLoanPrefix(ld.loanId) === normalizedRouteLoanId,
    );
    const loanSpecificBorrowerId =
      loanDetail?.borrowers?.find((id) => borrower?.borrowerIds?.includes(id)) ??
      loanDetail?.borrowers?.[0];
    const borrowerId = selectedBorrower
      ? (loanSpecificBorrowerId || selectedBorrower.borrowerIds?.[0] || selectedBorrower.borrowerInfoId || dashboardBorrowerId || '')
      : (dashboardBorrowerId || loanSpecificBorrowerId || borrower?.borrowerIds?.[0] || borrower?.borrowerInfoId || '');
    const finalBorrowerId = borrowerId || validMlaBorrowerId || '';

    if (!finalBorrowerId) {
      loggingService.logWarning('[OriginationAuthGuard] No borrowerId found in borrower info, redirecting to dashboard');
      return router.createUrlTree(['/dashboard']);
    }

    const mlaSsoToken = await mlaAuthService.getSsoTokenForCp(loanId, finalBorrowerId);
    if (!mlaSsoToken) {
      loggingService.logWarning('[OriginationAuthGuard] Failed to get MLA SSO token, redirecting to dashboard');
      return router.createUrlTree(['/dashboard']);
    }

    const cpResponse = await ssoAuthService.login({ ssoToken: mlaSsoToken });

    authService.setCpContext({
      authToken: cpResponse.authToken,
      loanId: cpResponse.loanId,
      borrowerId: cpResponse.borrowerId,
      userGuid: cpResponse.userGuid,
      expiresInSeconds: cpResponse.expiresInSeconds,
      visitorId: cpResponse.visitorId,
      mlaDomainLink: cpResponse.mlaDomainLink,
      email: cpResponse.email,
    });

    tokenRefreshService.startMonitoring();

    if (cpResponse.loanId && stripEpsLoanPrefix(cpResponse.loanId) !== stripEpsLoanPrefix(loanId)) {
      return router.createUrlTree(['/origination', cpResponse.loanId]);
    }

    return true;
  } catch (error) {
    loggingService.logException(error, '[OriginationAuthGuard] Auth flow failed, redirecting to dashboard');
    return router.createUrlTree(['/dashboard']);
  }
};
